import {
  UserMultiple02Icon,
  DashboardSquare01Icon,
  Building03Icon,
  Settings02Icon,
  Note04Icon,
  Medal06Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useFeatureEntitlements } from "@frontegg/react";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import usePlatformRewardsAvailable from "hooks/usePlatformRewardsAvailable";
import type {
  NavItemConfig,
  SubNavItemConfig,
} from "components/nav/AppSidebar";

/**
 * Nav config for the Platform shell (`/platform/*`).
 *
 * A hook rather than a component because the visibility rules need one; the
 * rendering is `AppSidebar`'s job, and every shell shares it.
 */
export function usePlatformSidebarItems(): {
  items: NavItemConfig[];
  subItems: SubNavItemConfig[];
} {
  const isPlatformRoles = useCheckUserRole(Role.PlatformAdmin);
  const platformRewardsAvailable = usePlatformRewardsAvailable();
  const canManageBranding = useCheckUserRole(Role.PlatformOwner);
  const { isEntitled: featureApiAccess } =
    useFeatureEntitlements("feature_api_access");

  const sidebarItems: NavItemConfig[] = [
    {
      IconComponent: DashboardSquare01Icon,
      name: "Dashboard",
      url: "dashboard",
      isVisible: isPlatformRoles,
    },
    {
      IconComponent: Building03Icon,
      name: "Accounts",
      url: "accounts",
      isVisible: isPlatformRoles,
    },
    {
      IconComponent: Medal06Icon,
      name: "Rewards",
      url: "rewards",
      isVisible: platformRewardsAvailable,
    },
    {
      IconComponent: UserMultiple02Icon,
      name: "Users",
      url: "users",
      isVisible: isPlatformRoles,
    },
    {
      IconComponent: Note04Icon,
      name: "Event Logs",
      url: "event-logs",
      isVisible: isPlatformRoles,
    },
    {
      IconComponent: Settings02Icon,
      name: "Settings",
      url: "settings",
      isVisible: isPlatformRoles && (canManageBranding || featureApiAccess),
      subItemDisplayType: "drawer",
    },
  ];

  // The same gates the routes use in `App.tsx`.
  const subSidebarItems: SubNavItemConfig[] = [
    {
      name: "Branding",
      url: "branding",
      parentUrl: "settings",
      isVisible: canManageBranding,
    },
    {
      name: "API Access",
      url: "api-access",
      parentUrl: "settings",
      isVisible: featureApiAccess,
    },
    {
      name: "Webhooks",
      url: "webhooks",
      parentUrl: "settings",
      isVisible: featureApiAccess,
    },
    {
      name: "Embedded components",
      url: "embedded-components",
      parentUrl: "settings",
      isVisible: featureApiAccess,
    },
    {
      name: "Integrations",
      url: "integrations",
      parentUrl: "settings",
      isVisible: canManageBranding,
    },
  ];

  return { items: sidebarItems, subItems: subSidebarItems };
}

export default usePlatformSidebarItems;
